import fs from 'node:fs/promises';
import path from 'node:path';
import type { RunData } from '@chatsdet/diagnostic-workspace';
import { materializeRunWorkspace } from './workflow-debug.js';

export interface DryRunData {
  runId: string;
  workflowId: string;
  orgId: string;
  metadataJson: Record<string, unknown> | null;
  steps: RunData['steps'];
  nodes: RunData['nodes'];
}

export interface WorkspaceManifestLike {
  runId: string;
  workflowId: string;
  orgId: string;
}

export async function ensureOptimizeDryWorkspace(
  args: { runId: string; workflowId?: string; outDir: string },
  apiUrl: string,
  token: string,
): Promise<{ workspace: string; runId: string; workflowId: string; runData: DryRunData }> {
  const existing = await readWorkspaceManifest(args.outDir);
  if (
    existing &&
    existing.runId === args.runId &&
    (!args.workflowId || existing.workflowId === args.workflowId)
  ) {
    const runData = await loadDryRunDataFromWorkspace(args.outDir, existing);
    return {
      workspace: args.outDir,
      runId: existing.runId,
      workflowId: existing.workflowId,
      runData,
    };
  }

  const materialized = await materializeRunWorkspace({
    runId: args.runId,
    workflowId: args.workflowId,
    outDir: args.outDir,
    apiUrl,
    token,
    onWarning: (message) => console.warn(message),
  });
  await writeRunDataCache(args.outDir, materialized.runData);
  return {
    workspace: materialized.workspace,
    runId: materialized.runId,
    workflowId: materialized.workflowId,
    runData: toDryRunData(materialized.runData),
  };
}

export async function readWorkspaceManifest(dir: string): Promise<WorkspaceManifestLike | null> {
  const manifestPath = path.join(dir, 'manifest.json');
  try {
    const raw = await fs.readFile(manifestPath, 'utf-8');
    const parsed = JSON.parse(raw) as unknown;
    if (!isObject(parsed)) return null;
    if (
      typeof parsed.runId === 'string' &&
      typeof parsed.workflowId === 'string' &&
      typeof parsed.orgId === 'string'
    ) {
      return { runId: parsed.runId, workflowId: parsed.workflowId, orgId: parsed.orgId };
    }
    return null;
  } catch {
    return null;
  }
}

export async function loadDryRunDataFromWorkspace(
  workspaceRoot: string,
  manifest: WorkspaceManifestLike,
): Promise<DryRunData> {
  const cachedPath = path.join(workspaceRoot, 'optimizer', 'run-data.json');
  try {
    const raw = await fs.readFile(cachedPath, 'utf-8');
    return toDryRunData(JSON.parse(raw) as RunData);
  } catch {
    return reconstructDryRunDataFromWorkspace(workspaceRoot, manifest);
  }
}

export async function writeRunDataCache(workspaceRoot: string, runData: RunData): Promise<void> {
  const dir = path.join(workspaceRoot, 'optimizer');
  await fs.mkdir(dir, { recursive: true });
  await fs.writeFile(
    path.join(dir, 'run-data.json'),
    `${JSON.stringify(runData, null, 2)}\n`,
    'utf-8',
  );
}

export function toDryRunData(runData: RunData): DryRunData {
  return {
    runId: runData.runId,
    workflowId: runData.workflowId,
    orgId: runData.orgId,
    metadataJson: runData.metadataJson,
    steps: runData.steps,
    nodes: runData.nodes,
  };
}

async function reconstructDryRunDataFromWorkspace(
  workspaceRoot: string,
  manifest: WorkspaceManifestLike,
): Promise<DryRunData> {
  const snapshot = await readJsonFile(path.join(workspaceRoot, 'workflow', 'snapshot.json'));
  const steps = await readJsonlFile(path.join(workspaceRoot, 'timeline', 'steps.jsonl'));
  const nodes =
    isObject(snapshot) && Array.isArray(snapshot.nodes)
      ? snapshot.nodes
          .map(readWorkspaceNode)
          .filter((node): node is RunData['nodes'][number] => node !== null)
      : [];
  return {
    runId: manifest.runId,
    workflowId: manifest.workflowId,
    orgId: manifest.orgId,
    metadataJson: await readOptionalJsonObject(
      path.join(workspaceRoot, 'variables', 'inputs.json'),
    ),
    steps: steps
      .map(readWorkspaceStep)
      .filter((step): step is RunData['steps'][number] => step !== null),
    nodes,
  };
}

export async function assertReadableTrace(traceZipPath: string): Promise<void> {
  try {
    await fs.access(traceZipPath);
  } catch {
    throw new Error(`trace.zip is not readable at ${traceZipPath}`);
  }
}

async function readJsonFile(filePath: string): Promise<unknown> {
  return JSON.parse(await fs.readFile(filePath, 'utf-8')) as unknown;
}

async function readOptionalJsonObject(filePath: string): Promise<Record<string, unknown> | null> {
  try {
    const parsed = await readJsonFile(filePath);
    return isObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export async function readJsonlFile(filePath: string): Promise<unknown[]> {
  const raw = await fs.readFile(filePath, 'utf-8').catch(() => '');
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => JSON.parse(line) as unknown);
}

function readWorkspaceNode(value: unknown): RunData['nodes'][number] | null {
  if (!isObject(value)) return null;
  if (
    typeof value.id !== 'string' ||
    typeof value.nodeType !== 'string' ||
    typeof value.orderIndex !== 'number'
  ) {
    return null;
  }
  return {
    id: value.id,
    nodeType: value.nodeType,
    configJson: value.configJson,
    orderIndex: value.orderIndex,
    summary: typeof value.summary === 'string' ? value.summary : value.nodeType,
  };
}

function readWorkspaceStep(value: unknown): RunData['steps'][number] | null {
  if (!isObject(value)) return null;
  if (
    typeof value.id !== 'string' ||
    typeof value.orderIndex !== 'number' ||
    typeof value.status !== 'string'
  ) {
    return null;
  }
  const metadataJson: Record<string, unknown> = {};
  for (const key of [
    'cacheOutcome',
    'cacheHitSource',
    'cacheProcessingStatus',
    'cacheOutcomeReason',
    'executedPlaybook',
  ]) {
    if (value[key] !== null && value[key] !== undefined) metadataJson[key] = value[key];
  }
  return {
    id: value.id,
    nodeId: typeof value.nodeId === 'string' ? value.nodeId : '',
    status: value.status,
    startedAt: typeof value.startedAt === 'string' ? value.startedAt : null,
    finishedAt: typeof value.finishedAt === 'string' ? value.finishedAt : null,
    errorMessage: typeof value.errorMessage === 'string' ? value.errorMessage : null,
    orderIndex: value.orderIndex,
    metadataJson,
    nodeName: typeof value.name === 'string' ? value.name : null,
    nodeType: typeof value.nodeType === 'string' ? value.nodeType : null,
  };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
